import { Box, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { setSelectedProject } from '../../actions/forms';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import BookmarkOutlinedIcon from '@mui/icons-material/BookmarkOutlined';

/**
 * Component to standardize the styling of the project cards. Takes in several props to deal with updating project state, or handling navigation, depending on the variant.
 * @param {Object} props 
 * @param {Array} props.projects Array to loop over, generated a card per project object.
 * @param {number} props.projectId Uses the current projectId to check if it is the one selected.
 * @param {(projectId: number) => void} props.setProjectId
 * @param {(setProjectKey: number) => void} props.setProjectKey
 * @param {(projectMeasureType: string) => void} props.setProjectMeasureType
 * @param {(nickname: string) => void} props.setNickname
 * @param {(project: Object) => void} [props.handleNavigate] Links to the Biochar Productions Page. Required if variant is "link"
 * @param {string} props.variant "main" if it changes project state, "link" if it links to the project location.
 * @returns {JSX.Element} 
 */

export default function ProjectCard({ projects, projectId, setProjectId, setProjectKey, setProjectMeasureType, setNickname, handleNavigate, variant }) {
    const dispatch = useDispatch()
    const cardHeaderFontSize = '20px'
    const cardTextFontSize = '17px'
    return (
        <>
            {projects?.map((project, index) => (
                <Box className="biochar-project" key={index}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        maxWidth: 350,
                        ...(projectId === project.projectId &&
                            { background: 'linear-gradient(to bottom right, #535988, #313551)', color: '#FFF', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px' }),
                        ...(variant === 'link' &&
                            { ":hover": { background: 'linear-gradient(to bottom right, #535988, #313551)', color: '#FFF', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px' } })
                    }}
                    onClick={() => {
                        if (variant === 'main') {
                            dispatch(setSelectedProject({ projectId: project.projectId, id: project.id, measureType: project.measurementType, nickname: project.nickname }))
                            setProjectId(project.projectId);
                            setNickname(project.nickname);
                            setProjectKey(project.id)
                            setProjectMeasureType(project.measurementType)
                        }
                        else if (variant === 'link') {
                            handleNavigate(project)
                        }
                    }}>
                    <Box>
                        <Box sx={{ display: 'flex', pb: 3, justifyItems: 'center' }}>
                            <Typography sx={{ display: 'flex', placeItems: 'center', maxHeight: '33px' }}>{projectId === project.projectId ? <BookmarkOutlinedIcon /> : <BookmarkBorderOutlinedIcon />}</Typography>
                            <Typography
                                sx={{
                                    fontSize: cardHeaderFontSize,
                                    minWidth: 0,            // override flex default min-width: auto so it can shrink
                                    overflowWrap: 'anywhere', // break long unbroken tokens
                                    wordBreak: 'break-word',  // safety for older rendering
                                }}
                            >
                                {project.nickname}
                            </Typography>
                        </Box>
                    </Box>
                    <Box>
                        {project.nickname &&
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                <Typography sx={{ fontWeight: 'bold', fontSize: cardTextFontSize }}>Project ID: </Typography>
                                <Typography sx={{ fontSize: cardTextFontSize, fontWeight: 'bold' }}>{project.projectId}</Typography>
                            </Box>
                        }
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Typography sx={{ fontWeight: 'bold', fontSize: cardTextFontSize }}>Unit System: </Typography>
                            <Typography sx={{ fontSize: cardTextFontSize }}>{project.measurementType}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Typography sx={{ fontWeight: 'bold', fontSize: cardTextFontSize }}>Date Created: </Typography>
                            <Typography sx={{ fontSize: cardTextFontSize }}>{project.created_at.split('T')[0]}</Typography>
                        </Box>
                    </Box>
                </Box>
            ))
            }
        </>
    )
}
